import React, { useState, useEffect, useMemo, useCallback } from "react";
import api from "../api/axios";
import { usePermissions } from "../../context/PermissionContext";
import { Modal, Spinner, OverlayTrigger, Tooltip } from "react-bootstrap";
import {
  FaShieldAlt,
  FaSync,
  FaCopy,
  FaUsers,
  FaSave,
  FaSearch,
  FaCheckCircle,
  FaTimesCircle,
  FaUserTie,
  FaSlidersH,
  FaSitemap,
  FaUsersCog,
  FaIdBadge,
  FaChartLine,
  FaPlus,
  FaTrash,
  FaEdit,
  FaCrown,
  FaUserFriends,
  FaLayerGroup,
  FaCheck,
  FaInfoCircle
} from "react-icons/fa";
import "./PermissionsPage.css";

const PermissionsPage = () => {
  const {
    switchActiveEmployee,
    activeEmployeeId,
    reloadActivePermissions,
    reloadEmployees,
  } = usePermissions();

  // Active Main Tab
  const [activeTab, setActiveTab] = useState("modules"); // 'modules' | 'hierarchy' | 'teams' | 'designations' | 'stats'

  // Loading & Saving
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toastMsg, setToastMsg] = useState("");

  // Metadata & Employees
  const [metadata, setMetadata] = useState({ categories: [], modules: [], roles: {}, hierarchy_levels: [] });
  const [employees, setEmployees] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");

  // Module Permissions State
  const [roleName, setRoleName] = useState("Custom");
  const [isAdmin, setIsAdmin] = useState(false);
  const [permissions, setPermissions] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");

  // Hierarchy State for Selected Employee
  const [designationId, setDesignationId] = useState("");
  const [hierarchyLevel, setHierarchyLevel] = useState("subordinate");
  const [managerId, setManagerId] = useState("");
  const [teamId, setTeamId] = useState("");

  // Designations State
  const [designations, setDesignations] = useState([]);
  const [showDesigModal, setShowDesigModal] = useState(false);
  const [desigForm, setDesigForm] = useState({ title: "", hierarchy_level: "subordinate", description: "" });
  const [editingDesigId, setEditingDesigId] = useState(null);

  // Teams & Tagging State
  const [teams, setTeams] = useState([]);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [teamForm, setTeamForm] = useState({ name: "", department: "", description: "", team_manager_id: "", member_ids: [] });
  const [editingTeamId, setEditingTeamId] = useState(null);
  const [myTeamData, setMyTeamData] = useState(null);

  // Team Stats & Reports State
  const [statsData, setStatsData] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsTeamFilter, setStatsTeamFilter] = useState("");

  // Hierarchy Tree State
  const [hierarchyTree, setHierarchyTree] = useState({ employees: [], teams: [] });

  // Clone Modal
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [sourceEmpId, setSourceEmpId] = useState("");

  // Show Toast
  const showToast = (msg) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(""), 3500);
  };

  // 1. Initial Load: Metadata, Employees, Designations, Teams
  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      const [metaRes, empRes, desigRes, teamRes] = await Promise.all([
        api.get("/permissions/meta"),
        api.get("/permissions/employees"),
        api.get("/permissions/designations"),
        api.get("/permissions/teams"),
      ]);
      setMetadata(metaRes.data || { categories: [], modules: [], roles: {}, hierarchy_levels: [] });
      const empList = empRes.data || [];
      setEmployees(empList);
      setDesignations(desigRes.data || []);
      setTeams(teamRes.data || []);

      const initialId = activeEmployeeId || (empList.length > 0 ? String(empList[0].id) : "");
      setSelectedEmployeeId(initialId);
    } catch (err) {
      console.error("Failed to load initial permissions data", err);
    } finally {
      setLoading(false);
    }
  }, [activeEmployeeId]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // 2. Load Permissions & Hierarchy for Selected Employee
  const loadEmployeeDetails = useCallback(async (empId) => {
    if (!empId) return;
    try {
      setLoading(true);
      const [permRes, myTeamRes] = await Promise.all([
        api.get(`/permissions/employee/${empId}`),
        api.get(`/permissions/my-team/${empId}`),
      ]);

      if (permRes.data) {
        setRoleName(permRes.data.role_name || "Custom");
        setIsAdmin(Boolean(permRes.data.is_admin));
        setPermissions(permRes.data.permissions || {});
        setDesignationId(permRes.data.designation_id ? String(permRes.data.designation_id) : "");
        setHierarchyLevel(permRes.data.hierarchy_level || "subordinate");
        setManagerId(permRes.data.manager_id ? String(permRes.data.manager_id) : "");
        setTeamId(permRes.data.team_id ? String(permRes.data.team_id) : "");
      }

      if (myTeamRes.data) {
        setMyTeamData(myTeamRes.data);
      }
    } catch (err) {
      console.error("Failed to fetch employee permissions", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedEmployeeId) {
      loadEmployeeDetails(selectedEmployeeId);
    }
  }, [selectedEmployeeId, loadEmployeeDetails]);

  // 3. Load Hierarchy Tree when Hierarchy tab is opened
  const loadHierarchyTree = useCallback(async () => {
    try {
      const res = await api.get("/permissions/hierarchy/tree");
      setHierarchyTree(res.data || { employees: [], teams: [] });
    } catch (err) {
      console.error("Failed to load hierarchy tree", err);
    }
  }, []);

  // 4. Load Team Stats when Stats tab is opened
  const loadStats = useCallback(async (empId, tId = "") => {
    if (!empId) return;
    try {
      setStatsLoading(true);
      const url = tId
        ? `/permissions/hierarchy/stats/${empId}?team_id=${tId}`
        : `/permissions/hierarchy/stats/${empId}`;
      const res = await api.get(url);
      setStatsData(res.data);
    } catch (err) {
      console.error("Failed to load team stats", err);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === "hierarchy") {
      loadHierarchyTree();
    } else if (activeTab === "stats" && selectedEmployeeId) {
      loadStats(selectedEmployeeId, statsTeamFilter);
    }
  }, [activeTab, selectedEmployeeId, statsTeamFilter, loadHierarchyTree, loadStats]);

  // Selected employee object
  const currentEmployee = useMemo(() => {
    return employees.find((e) => String(e.id) === String(selectedEmployeeId)) || null;
  }, [employees, selectedEmployeeId]);

  const handleEmployeeChange = (e) => {
    const newId = e.target.value;
    setSelectedEmployeeId(newId);
    switchActiveEmployee(newId);
  };

  // Preset Role Apply
  const handleApplyRole = (chosenRole) => {
    setRoleName(chosenRole);
    if (chosenRole === "Administrator" || chosenRole === "Director") {
      setIsAdmin(true);
      setHierarchyLevel("management");
    } else if (chosenRole === "Manager") {
      setIsAdmin(false);
      setHierarchyLevel("manager");
    } else {
      setIsAdmin(false);
    }

    const preset = metadata.roles?.[chosenRole];
    if (preset) {
      setPermissions(JSON.parse(JSON.stringify(preset)));
      showToast(`Applied preset role: ${chosenRole}`);
    } else if (chosenRole === "Administrator" || chosenRole === "Director") {
      const full = {};
      metadata.modules.forEach((mod) => {
        full[mod.key] = {};
        mod.actions.forEach((act) => {
          full[mod.key][act] = true;
        });
      });
      setPermissions(full);
      showToast(`Granted full administrative access`);
    }
  };

  // Toggle single cell permission
  const handleToggleAction = (moduleKey, action) => {
    setPermissions((prev) => {
      const modObj = prev[moduleKey] ? { ...prev[moduleKey] } : {};
      const currentVal = modObj[action] !== undefined ? modObj[action] : true;
      modObj[action] = !currentVal;
      return {
        ...prev,
        [moduleKey]: modObj,
      };
    });
    setRoleName("Custom");
  };

  // Toggle entire module row
  const handleToggleRow = (moduleKey, actions) => {
    setPermissions((prev) => {
      const modObj = prev[moduleKey] || {};
      const allActive = actions.every((act) => Boolean(modObj[act]));
      const newVal = !allActive;
      const updatedMod = {};
      actions.forEach((act) => {
        updatedMod[act] = newVal;
      });
      return {
        ...prev,
        [moduleKey]: updatedMod,
      };
    });
    setRoleName("Custom");
  };

  // Toggle entire column
  const handleToggleColumn = (action) => {
    const visibleMods = filteredModules.filter((m) => m.actions.includes(action));
    const allActive = visibleMods.every((m) => Boolean(permissions[m.key]?.[action]));
    const newVal = !allActive;

    setPermissions((prev) => {
      const next = { ...prev };
      visibleMods.forEach((m) => {
        next[m.key] = {
          ...(next[m.key] || {}),
          [action]: newVal,
        };
      });
      return next;
    });
    setRoleName("Custom");
    showToast(`${newVal ? "Enabled" : "Disabled"} '${action}' for visible modules`);
  };

  // Batch Grant All / Revoke All
  const handleBatchAll = (grantAll) => {
    const updated = {};
    metadata.modules.forEach((mod) => {
      updated[mod.key] = {};
      mod.actions.forEach((act) => {
        updated[mod.key][act] = grantAll;
      });
    });
    setPermissions(updated);
    setIsAdmin(grantAll);
    if (grantAll) setHierarchyLevel("management");
    setRoleName(grantAll ? "Administrator" : "Restricted Custom");
    showToast(grantAll ? "Granted all CRM permissions" : "Revoked all CRM permissions");
  };

  // Save changes to backend
  const handleSave = async () => {
    if (!selectedEmployeeId) {
      alert("Please select an employee first.");
      return;
    }
    try {
      setSaving(true);
      await api.post(`/permissions/employee/${selectedEmployeeId}`, {
        role_name: roleName,
        is_admin: isAdmin,
        designation_id: designationId ? Number(designationId) : null,
        hierarchy_level: hierarchyLevel,
        manager_id: managerId ? Number(managerId) : null,
        team_id: teamId ? Number(teamId) : null,
        permissions: permissions,
      });

      await reloadActivePermissions();
      await reloadEmployees();
      showToast(`Permissions and hierarchy successfully saved for ${currentEmployee?.name || "employee"}!`);
    } catch (err) {
      console.error("Save permissions error", err);
      alert("Failed to save permissions. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Clone from another employee
  const handleClone = async () => {
    if (!sourceEmpId || !selectedEmployeeId) {
      alert("Please select a source employee.");
      return;
    }
    try {
      setLoading(true);
      const res = await api.post(
        `/permissions/employee/${selectedEmployeeId}/clone-from/${sourceEmpId}`
      );
      if (res.data) {
        setPermissions(res.data.permissions || {});
        setRoleName(res.data.role_name || "Custom");
        if (res.data.hierarchy_level) setHierarchyLevel(res.data.hierarchy_level);
        setShowCloneModal(false);
        showToast("Permissions cloned successfully!");
      }
    } catch (err) {
      console.error("Clone error", err);
      alert("Failed to clone permissions.");
    } finally {
      setLoading(false);
    }
  };

  // -------------------------------------------------------------
  // DESIGNATION ACTIONS
  // -------------------------------------------------------------
  const handleOpenAddDesig = () => {
    setEditingDesigId(null);
    setDesigForm({ title: "", hierarchy_level: "subordinate", description: "" });
    setShowDesigModal(true);
  };

  const handleOpenEditDesig = (desig) => {
    setEditingDesigId(desig.id);
    setDesigForm({
      title: desig.title,
      hierarchy_level: desig.hierarchy_level,
      description: desig.description || "",
    });
    setShowDesigModal(true);
  };

  const handleSaveDesignation = async () => {
    if (!desigForm.title.trim()) {
      alert("Designation title is required.");
      return;
    }
    try {
      setLoading(true);
      if (editingDesigId) {
        await api.put(`/permissions/designations/${editingDesigId}`, desigForm);
        showToast(`Designation '${desigForm.title}' updated successfully!`);
      } else {
        await api.post("/permissions/designations", desigForm);
        showToast(`Designation '${desigForm.title}' created successfully!`);
      }
      setShowDesigModal(false);
      const res = await api.get("/permissions/designations");
      setDesignations(res.data || []);
    } catch (err) {
      console.error("Save designation error", err);
      alert("Failed to save designation. Name may already exist.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDesignation = async (desig) => {
    if (desig.is_default) {
      alert("Cannot delete default system designation.");
      return;
    }
    if (!window.confirm(`Are you sure you want to delete designation '${desig.title}'?`)) {
      return;
    }
    try {
      setLoading(true);
      await api.delete(`/permissions/designations/${desig.id}`);
      showToast(`Designation '${desig.title}' deleted successfully.`);
      const res = await api.get("/permissions/designations");
      setDesignations(res.data || []);
    } catch (err) {
      console.error("Delete designation error", err);
      alert("Failed to delete designation.");
    } finally {
      setLoading(false);
    }
  };

  // -------------------------------------------------------------
  // TEAM ACTIONS & TEAM TAGGING
  // -------------------------------------------------------------
  const handleOpenAddTeam = () => {
    setEditingTeamId(null);
    setTeamForm({ name: "", department: "", description: "", team_manager_id: "", member_ids: [] });
    setShowTeamModal(true);
  };

  const handleOpenEditTeam = (team) => {
    setEditingTeamId(team.id);
    const existingMemberIds = (team.members || []).map((m) => m.employee_id);
    setTeamForm({
      name: team.name,
      department: team.department || "",
      description: team.description || "",
      team_manager_id: team.team_manager_id ? String(team.team_manager_id) : "",
      member_ids: existingMemberIds,
    });
    setShowTeamModal(true);
  };

  const handleSaveTeam = async () => {
    if (!teamForm.name.trim()) {
      alert("Team name is required.");
      return;
    }
    try {
      setLoading(true);
      const payload = {
        name: teamForm.name,
        department: teamForm.department,
        description: teamForm.description,
        team_manager_id: teamForm.team_manager_id ? Number(teamForm.team_manager_id) : null,
        member_ids: teamForm.member_ids,
      };

      if (editingTeamId) {
        await api.put(`/permissions/teams/${editingTeamId}`, payload);
        showToast(`Team '${teamForm.name}' updated successfully!`);
      } else {
        await api.post("/permissions/teams", payload);
        showToast(`Team '${teamForm.name}' created with tagged members!`);
      }
      setShowTeamModal(false);
      const res = await api.get("/permissions/teams");
      setTeams(res.data || []);
      // Refresh my team data if active employee is affected
      if (selectedEmployeeId) {
        const mtRes = await api.get(`/permissions/my-team/${selectedEmployeeId}`);
        setMyTeamData(mtRes.data);
      }
    } catch (err) {
      console.error("Save team error", err);
      alert("Failed to save team. Name may already exist.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTeam = async (team) => {
    if (!window.confirm(`Are you sure you want to delete team '${team.name}'?`)) {
      return;
    }
    try {
      setLoading(true);
      await api.delete(`/permissions/teams/${team.id}`);
      showToast(`Team '${team.name}' deleted.`);
      const res = await api.get("/permissions/teams");
      setTeams(res.data || []);
    } catch (err) {
      console.error("Delete team error", err);
      alert("Failed to delete team.");
    } finally {
      setLoading(false);
    }
  };

  const handleUntagMember = async (teamId, employeeId) => {
    try {
      await api.post("/permissions/teams/untag", {
        team_id: teamId,
        employee_id: employeeId,
      });
      showToast("Removed member from team.");
      const res = await api.get("/permissions/teams");
      setTeams(res.data || []);
      if (selectedEmployeeId) {
        const mtRes = await api.get(`/permissions/my-team/${selectedEmployeeId}`);
        setMyTeamData(mtRes.data);
      }
    } catch (err) {
      console.error("Untag error", err);
      alert("Failed to untag member.");
    }
  };

  // Filter modules
  const filteredModules = useMemo(() => {
    return (metadata.modules || []).filter((mod) => {
      const matchCat = activeCategory === "All" || mod.category === activeCategory;
      const q = searchQuery.toLowerCase().trim();
      const matchQuery =
        !q ||
        mod.name.toLowerCase().includes(q) ||
        mod.key.toLowerCase().includes(q) ||
        (mod.description && mod.description.toLowerCase().includes(q));
      return matchCat && matchQuery;
    });
  }, [metadata.modules, activeCategory, searchQuery]);

  // Statistics
  const stats = useMemo(() => {
    let totalActions = 0;
    let grantedActions = 0;
    metadata.modules.forEach((mod) => {
      mod.actions.forEach((act) => {
        totalActions++;
        if (permissions[mod.key]?.[act]) {
          grantedActions++;
        }
      });
    });
    return {
      totalModules: metadata.modules.length,
      totalActions,
      grantedActions,
      deniedActions: totalActions - grantedActions,
    };
  }, [metadata.modules, permissions]);

  const categoriesWithCounts = useMemo(() => {
    const list = ["All", ...(metadata.categories || [])];
    return list.map((cat) => {
      const count =
        cat === "All"
          ? metadata.modules.length
          : metadata.modules.filter((m) => m.category === cat).length;
      return { name: cat, count };
    });
  }, [metadata.categories, metadata.modules]);

  return (
    <div className="permissions-container">
      {/* Toast */}
      {toastMsg && (
        <div className="perm-toast">
          <FaCheckCircle style={{ color: "#4ade80", fontSize: "16px" }} />
          <span>{toastMsg}</span>
        </div>
      )}

      {/* Header */}
      <div className="perm-header">
        <div className="perm-header-left">
          <div className="perm-header-icon">
            <FaShieldAlt />
          </div>
          <div>
            <h2 className="perm-header-title">
              Permissions & Hierarchy: {currentEmployee?.name || "Employee"}
            </h2>
            <p className="perm-header-subtitle">
              Manage hierarchical permissions, team tagging, designations, and subordinate visibility across Rekonic CRM.
            </p>
          </div>
        </div>

        <div className="perm-header-actions">
          <button
            className="perm-btn perm-btn-default"
            onClick={() => loadEmployeeDetails(selectedEmployeeId)}
            title="Refresh current settings"
          >
            <FaSync /> Refresh
          </button>
          <button
            className="perm-btn perm-btn-default"
            onClick={() => setShowCloneModal(true)}
            title="Duplicate permissions from another user"
          >
            <FaCopy /> Duplicate
          </button>
          <button
            className="perm-btn perm-btn-success"
            onClick={handleSave}
            disabled={saving || loading}
          >
            {saving ? (
              <>
                <Spinner size="sm" animation="border" /> Saving...
              </>
            ) : (
              <>
                <FaSave /> Save Changes
              </>
            )}
          </button>
        </div>
      </div>

      {/* Main Tab Navigation */}
      <div className="perm-main-nav">
        <button
          className={`perm-main-tab-btn ${activeTab === "modules" ? "active" : ""}`}
          onClick={() => setActiveTab("modules")}
        >
          <FaShieldAlt /> Module Permissions
        </button>
        <button
          className={`perm-main-tab-btn ${activeTab === "hierarchy" ? "active" : ""}`}
          onClick={() => setActiveTab("hierarchy")}
        >
          <FaSitemap /> Organizational Hierarchy Tree
        </button>
        <button
          className={`perm-main-tab-btn ${activeTab === "teams" ? "active" : ""}`}
          onClick={() => setActiveTab("teams")}
        >
          <FaUsersCog /> Team Tagging & Squads
        </button>
        <button
          className={`perm-main-tab-btn ${activeTab === "designations" ? "active" : ""}`}
          onClick={() => setActiveTab("designations")}
        >
          <FaIdBadge /> Designations Directory
        </button>
        <button
          className={`perm-main-tab-btn ${activeTab === "stats" ? "active" : ""}`}
          onClick={() => setActiveTab("stats")}
        >
          <FaChartLine /> Team Stats & Reports
        </button>
      </div>

      {/* Global Employee Switcher Bar */}
      <div className="perm-controls-card">
        <div className="perm-controls-row">
          <div className="perm-emp-select-wrap">
            <span className="perm-emp-label">
              <FaUserTie style={{ marginRight: "6px", color: "#0284c7" }} /> Employee:
            </span>
            <select
              className="perm-select"
              value={selectedEmployeeId}
              onChange={handleEmployeeChange}
            >
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.name} ({emp.designation_title || emp.role_name || "Staff"}) - [{emp.hierarchy_level || "subordinate"}]
                </option>
              ))}
            </select>

            <span className={`badge-hierarchy ${hierarchyLevel}`}>
              {hierarchyLevel === "management" && <FaCrown style={{ marginRight: "4px" }} />}
              {hierarchyLevel === "manager" && <FaLayerGroup style={{ marginRight: "4px" }} />}
              {hierarchyLevel === "team_manager" && <FaUserFriends style={{ marginRight: "4px" }} />}
              {hierarchyLevel.replace("_", " ")}
            </span>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
            {/* Quick Designation Picker */}
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span className="perm-emp-label">Designation:</span>
              <select
                className="perm-select"
                style={{ minWidth: "160px" }}
                value={designationId}
                onChange={(e) => {
                  const val = e.target.value;
                  setDesignationId(val);
                  const found = designations.find((d) => String(d.id) === val);
                  if (found) {
                    setHierarchyLevel(found.hierarchy_level);
                  }
                }}
              >
                <option value="">-- Choose Designation --</option>
                {designations.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.title} ({d.hierarchy_level})
                  </option>
                ))}
              </select>
            </div>

            {/* Direct Reporting Manager */}
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span className="perm-emp-label">Reports To (Manager):</span>
              <select
                className="perm-select"
                style={{ minWidth: "160px" }}
                value={managerId}
                onChange={(e) => setManagerId(e.target.value)}
              >
                <option value="">None (Top Level / Self)</option>
                {employees
                  .filter((e) => String(e.id) !== String(selectedEmployeeId))
                  .map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.name} ({e.designation_title || "Manager"})
                    </option>
                  ))}
              </select>
            </div>

            {/* Primary Team */}
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span className="perm-emp-label">Team:</span>
              <select
                className="perm-select"
                style={{ minWidth: "150px" }}
                value={teamId}
                onChange={(e) => setTeamId(e.target.value)}
              >
                <option value="">-- No Team --</option>
                {teams.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* ========================================================= */}
      {/* TAB 1: MODULE PERMISSIONS MATRIX                          */}
      {/* ========================================================= */}
      {activeTab === "modules" && (
        <>
          <div className="perm-controls-card" style={{ padding: "14px 20px" }}>
            <div className="perm-controls-row">
              {/* Preset Selector */}
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span className="perm-emp-label">
                  <FaSlidersH style={{ marginRight: "6px", color: "#64748b" }} /> Preset Role:
                </span>
                <select
                  className="perm-select"
                  style={{ minWidth: "160px" }}
                  value={roleName}
                  onChange={(e) => handleApplyRole(e.target.value)}
                >
                  <option value="Administrator">Administrator (Full Access)</option>
                  <option value="Director">Director</option>
                  <option value="Manager">Manager</option>
                  <option value="Accountant">Accountant</option>
                  <option value="Sales Executive">Sales Executive</option>
                  <option value="Auditor / Viewer">Auditor / Viewer</option>
                  <option value="Custom">Custom</option>
                </select>
              </div>

              {/* Search Box */}
              <div className="perm-search-box">
                <FaSearch className="perm-search-icon" />
                <input
                  type="text"
                  placeholder="Search CRM module..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            {/* Category Tabs */}
            <div className="perm-tabs">
              {categoriesWithCounts.map((cat) => (
                <button
                  key={cat.name}
                  className={`perm-tab-btn ${activeCategory === cat.name ? "active" : ""}`}
                  onClick={() => setActiveCategory(cat.name)}
                >
                  {cat.name} ({cat.count})
                </button>
              ))}
            </div>
          </div>

          {/* Batch Bar */}
          <div className="perm-batch-bar">
            <div className="perm-stats">
              Showing <strong>{filteredModules.length}</strong> modules |{" "}
              <span style={{ color: "#16a34a", fontWeight: "600" }}>{stats.grantedActions} Allowed</span> |{" "}
              <span style={{ color: "#dc2626", fontWeight: "600" }}>{stats.deniedActions} Denied</span>
            </div>
            <div className="perm-batch-actions">
              <button className="perm-btn-sm" onClick={() => handleBatchAll(true)}>
                <FaCheckCircle style={{ color: "#16a34a", marginRight: "4px" }} /> Grant All
              </button>
              <button className="perm-btn-sm" onClick={() => handleBatchAll(false)}>
                <FaTimesCircle style={{ color: "#dc2626", marginRight: "4px" }} /> Revoke All
              </button>
            </div>
          </div>

          {/* Table Card */}
          <div className="perm-table-card">
            {loading ? (
              <div style={{ padding: "60px", textAlign: "center" }}>
                <Spinner animation="border" variant="primary" />
                <p style={{ marginTop: "12px", color: "#64748b" }}>Loading permission matrix...</p>
              </div>
            ) : (
              <table className="perm-table">
                <thead>
                  <tr>
                    <th style={{ width: "32%" }}>Module</th>
                    <th className="center-col" style={{ width: "11%" }}>
                      <span className="perm-col-toggle" onClick={() => handleToggleColumn("view")}>
                        View / Access
                      </span>
                    </th>
                    <th className="center-col" style={{ width: "11%" }}>
                      <span className="perm-col-toggle" onClick={() => handleToggleColumn("create")}>
                        Create
                      </span>
                    </th>
                    <th className="center-col" style={{ width: "11%" }}>
                      <span className="perm-col-toggle" onClick={() => handleToggleColumn("edit")}>
                        Edit
                      </span>
                    </th>
                    <th className="center-col" style={{ width: "11%" }}>
                      <span className="perm-col-toggle" onClick={() => handleToggleColumn("delete")}>
                        Delete
                      </span>
                    </th>
                    <th className="center-col" style={{ width: "11%" }}>
                      <span className="perm-col-toggle" onClick={() => handleToggleColumn("export")}>
                        Export / PDF
                      </span>
                    </th>
                    <th className="center-col" style={{ width: "8%" }}>All</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredModules.length === 0 ? (
                    <tr>
                      <td colSpan={7} style={{ textAlign: "center", padding: "40px", color: "#94a3b8" }}>
                        No CRM modules matching search query.
                      </td>
                    </tr>
                  ) : (
                    filteredModules.map((mod) => {
                      const modPerm = permissions[mod.key] || {};
                      const actions = ["view", "create", "edit", "delete", "export"];
                      const allActive = mod.actions.every((act) => Boolean(modPerm[act]));

                      return (
                        <tr key={mod.key}>
                          <td>
                            <div className="perm-module-info">
                              <span className="perm-module-name">
                                {mod.name}
                                <span className="perm-cat-tag">{mod.category}</span>
                              </span>
                              <span className="perm-module-desc">{mod.description}</span>
                            </div>
                          </td>

                          {actions.map((act) => {
                            const isApplicable = mod.actions.includes(act);
                            if (!isApplicable) {
                              return (
                                <td key={act} className="center-col">
                                  <span className="perm-indicator na">
                                    <span className="perm-dot-na" />
                                  </span>
                                </td>
                              );
                            }

                            const isGranted = Boolean(modPerm[act]);
                            return (
                              <td key={act} className="center-col">
                                <OverlayTrigger
                                  placement="top"
                                  overlay={
                                    <Tooltip id={`tt-${mod.key}-${act}`}>
                                      {isGranted ? `Allowed: ${act}` : `Denied: ${act}`}
                                    </Tooltip>
                                  }
                                >
                                  <div
                                    className={`perm-indicator ${isGranted ? "granted" : "denied"}`}
                                    onClick={() => handleToggleAction(mod.key, act)}
                                  >
                                    {isGranted ? <span className="perm-dot-granted" /> : <span className="perm-dot-denied" />}
                                  </div>
                                </OverlayTrigger>
                              </td>
                            );
                          })}

                          <td className="center-col">
                            <button
                              className="perm-row-toggle"
                              onClick={() => handleToggleRow(mod.key, mod.actions)}
                            >
                              {allActive ? "All" : "Off"}
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            )}
          </div>

          {/* Legend */}
          <div className="perm-legend-card">
            <div className="perm-legend-items">
              <div className="perm-legend-item">
                <span className="perm-indicator granted" style={{ width: "20px", height: "20px" }}>
                  <span className="perm-dot-granted" style={{ width: "10px", height: "10px" }} />
                </span>
                <span><strong>Allowed</strong></span>
              </div>
              <div className="perm-legend-item">
                <span className="perm-indicator denied" style={{ width: "20px", height: "20px" }}>
                  <span className="perm-dot-denied" style={{ width: "10px", height: "10px" }} />
                </span>
                <span><strong>Denied</strong></span>
              </div>
              <div className="perm-legend-item">
                <span className="perm-indicator na" style={{ width: "20px", height: "20px" }}>
                  <span className="perm-dot-na" style={{ width: "8px", height: "2px" }} />
                </span>
                <span><strong>N/A</strong></span>
              </div>
            </div>
            <div style={{ fontSize: "12px", color: "#64748b" }}>
              Click any indicator to toggle access. Save Changes to apply across Rekonic.
            </div>
          </div>
        </>
      )}

      {/* ========================================================= */}
      {/* TAB 2: ORGANIZATIONAL HIERARCHY TREE                      */}
      {/* ========================================================= */}
      {activeTab === "hierarchy" && (
        <div>
          <div className="hierarchy-banner">
            <div className="hierarchy-banner-content">
              <h3>
                <FaSitemap style={{ color: "#38bdf8" }} /> Organization Hierarchy & Reporting Tree
              </h3>
              <p>
                Hierarchy defines who can see subordinate records, team stats, or company-wide data.
                Management/CEO sees all, Managers see all subordinates, Team Managers see team stats.
              </p>
            </div>
            <button className="perm-btn perm-btn-default" onClick={loadHierarchyTree}>
              <FaSync /> Reload Tree
            </button>
          </div>

          <div className="hierarchy-cards-grid">
            {hierarchyTree.employees.map((emp) => (
              <div
                key={emp.id}
                className="hierarchy-emp-card"
                style={{
                  borderLeft:
                    emp.hierarchy_level === "management"
                      ? "4px solid #a855f7"
                      : emp.hierarchy_level === "manager"
                      ? "4px solid #0284c7"
                      : emp.hierarchy_level === "team_manager"
                      ? "4px solid #0d9488"
                      : "4px solid #94a3b8",
                }}
              >
                <div className="hierarchy-emp-top">
                  <div className="hierarchy-avatar">
                    {emp.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="hierarchy-emp-meta">
                    <div className="hierarchy-emp-name">{emp.name}</div>
                    <div className="hierarchy-emp-desig">{emp.designation || "Staff"}</div>
                  </div>
                  <span className={`badge-hierarchy ${emp.hierarchy_level}`}>
                    {emp.hierarchy_level}
                  </span>
                </div>

                <div className="hierarchy-details-list">
                  <div className="hierarchy-detail-row">
                    <span>Direct Manager:</span>
                    <strong>{emp.manager_name || "None (Top Level)"}</strong>
                  </div>
                  <div className="hierarchy-detail-row">
                    <span>Assigned Team:</span>
                    <strong>{emp.team_name || "Unassigned"}</strong>
                  </div>
                  <div className="hierarchy-detail-row">
                    <span>System Role:</span>
                    <strong>{emp.is_admin ? "Administrator" : "Standard"}</strong>
                  </div>
                </div>

                <button
                  className="perm-btn-sm"
                  style={{ alignSelf: "flex-end" }}
                  onClick={() => {
                    setSelectedEmployeeId(String(emp.id));
                    setActiveTab("modules");
                  }}
                >
                  Configure Permissions →
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* TAB 3: TEAM TAGGING & SQUADS                              */}
      {/* ========================================================= */}
      {activeTab === "teams" && (
        <div>
          <div className="hierarchy-banner" style={{ background: "linear-gradient(135deg, #065f46, #047857)" }}>
            <div className="hierarchy-banner-content">
              <h3>
                <FaUsersCog style={{ color: "#a7f3d0" }} /> Team Tagging & Squads
              </h3>
              <p>
                Tag employees into teams so Team Managers can monitor team activities, stats, and reports.
                "Who is in my team, who is in your team".
              </p>
            </div>
            <button className="perm-btn perm-btn-default" onClick={handleOpenAddTeam}>
              <FaPlus /> Create New Team
            </button>
          </div>

          {/* "Who is in my team" preview for selected employee */}
          {myTeamData && (
            <div className="perm-controls-card" style={{ marginBottom: "24px", background: "#f0fdf4", border: "1px solid #bbf7d0" }}>
              <h4 style={{ fontSize: "16px", fontWeight: "700", color: "#166534", marginBottom: "8px", display: "flex", alignItems: "center", gap: "8px" }}>
                <FaUserFriends /> Team Membership for {currentEmployee?.name}
              </h4>
              <p style={{ fontSize: "13px", color: "#15803d", margin: 0 }}>
                {myTeamData.teams.length > 0 ? (
                  <>
                    Tagged in <strong>{myTeamData.teams.map((t) => t.name).join(", ")}</strong> with{" "}
                    <strong>{myTeamData.total_teammates}</strong> teammates.
                  </>
                ) : (
                  "Currently not tagged in any team."
                )}
              </p>
            </div>
          )}

          {/* Teams Grid */}
          <div className="teams-grid">
            {teams.map((t) => (
              <div key={t.id} className="team-card">
                <div>
                  <div className="team-card-header">
                    <div>
                      <h4 className="team-card-title">{t.name}</h4>
                      <span className="team-card-dept">{t.department || "General"}</span>
                    </div>
                    <div style={{ display: "flex", gap: "6px" }}>
                      <button className="perm-btn-sm" onClick={() => handleOpenEditTeam(t)} title="Edit Team">
                        <FaEdit />
                      </button>
                      <button className="perm-btn-sm" onClick={() => handleDeleteTeam(t)} title="Delete Team">
                        <FaTrash style={{ color: "#dc2626" }} />
                      </button>
                    </div>
                  </div>

                  <p style={{ fontSize: "13px", color: "#64748b", margin: "8px 0 14px 0" }}>
                    {t.description || "No description provided."}
                  </p>

                  <div className="team-manager-box">
                    <FaCrown />
                    <div>
                      <div style={{ fontSize: "11px", fontWeight: "700", textTransform: "uppercase" }}>Team Manager / Lead</div>
                      <div style={{ fontWeight: "700", fontSize: "14px" }}>
                        {t.team_manager_name || "Unassigned"}
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                    <span style={{ fontSize: "13px", fontWeight: "700", color: "#334155" }}>
                      Tagged Members ({t.member_count})
                    </span>
                    <button className="perm-btn-sm" onClick={() => handleOpenEditTeam(t)}>
                      + Tag Members
                    </button>
                  </div>

                  <div className="team-members-list">
                    {t.members.length === 0 ? (
                      <div style={{ fontSize: "12px", color: "#94a3b8", fontStyle: "italic", padding: "8px" }}>
                        No members tagged yet. Click '+ Tag Members'.
                      </div>
                    ) : (
                      t.members.map((m) => (
                        <div key={m.employee_id} className="team-member-item">
                          <div>
                            <strong>{m.name}</strong>
                            <span style={{ fontSize: "11px", color: "#64748b", marginLeft: "6px" }}>
                              ({m.designation || "Staff"})
                            </span>
                            {m.role === "lead" && (
                              <span style={{ fontSize: "10px", background: "#dcfce7", color: "#166534", padding: "2px 6px", borderRadius: "10px", marginLeft: "6px" }}>
                                Lead
                              </span>
                            )}
                          </div>
                          <button
                            className="member-untag-btn"
                            onClick={() => handleUntagMember(t.id, m.employee_id)}
                            title="Remove from team"
                          >
                            ×
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* TAB 4: DESIGNATION MANAGEMENT                             */}
      {/* ========================================================= */}
      {activeTab === "designations" && (
        <div>
          <div className="hierarchy-banner" style={{ background: "linear-gradient(135deg, #1e1b4b, #312e81)" }}>
            <div className="hierarchy-banner-content">
              <h3>
                <FaIdBadge style={{ color: "#a5b4fc" }} /> Designations & Hierarchy Levels
              </h3>
              <p>
                Configure job designations and their intrinsic hierarchy level (CEO/Management, Manager, Team Manager, Subordinate).
                Default designations are preserved while custom designations can be created flexibly.
              </p>
            </div>
            <button className="perm-btn perm-btn-default" onClick={handleOpenAddDesig}>
              <FaPlus /> Add Designation
            </button>
          </div>

          <div className="perm-table-card">
            <table className="perm-table">
              <thead>
                <tr>
                  <th>Designation Title</th>
                  <th>Hierarchy Level</th>
                  <th>Description</th>
                  <th>Employees</th>
                  <th>Type</th>
                  <th className="center-col">Action</th>
                </tr>
              </thead>
              <tbody>
                {designations.map((d) => (
                  <tr key={d.id}>
                    <td>
                      <strong style={{ fontSize: "14px", color: "#0f172a" }}>{d.title}</strong>
                    </td>
                    <td>
                      <span className={`badge-hierarchy ${d.hierarchy_level}`}>
                        {d.hierarchy_level}
                      </span>
                    </td>
                    <td style={{ color: "#64748b", fontSize: "12.5px" }}>
                      {d.description || "—"}
                    </td>
                    <td>
                      <span className="badge bg-light text-dark border">
                        {d.employee_count} {d.employee_count === 1 ? "Employee" : "Employees"}
                      </span>
                    </td>
                    <td>
                      {d.is_default ? (
                        <span style={{ fontSize: "11px", fontWeight: "700", background: "#e0f2fe", color: "#0369a1", padding: "3px 8px", borderRadius: "12px" }}>
                          Default System
                        </span>
                      ) : (
                        <span style={{ fontSize: "11px", fontWeight: "700", background: "#fef3c7", color: "#92400e", padding: "3px 8px", borderRadius: "12px" }}>
                          Custom
                        </span>
                      )}
                    </td>
                    <td className="center-col">
                      <div style={{ display: "flex", gap: "6px", justifyContent: "center" }}>
                        <button className="perm-btn-sm" onClick={() => handleOpenEditDesig(d)}>
                          <FaEdit />
                        </button>
                        {!d.is_default && (
                          <button className="perm-btn-sm" onClick={() => handleDeleteDesignation(d)}>
                            <FaTrash style={{ color: "#dc2626" }} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* TAB 5: TEAM STATS & REPORTS                               */}
      {/* ========================================================= */}
      {activeTab === "stats" && (
        <div>
          <div className="hierarchy-banner" style={{ background: "linear-gradient(135deg, #1e293b, #0f172a)" }}>
            <div className="hierarchy-banner-content">
              <h3>
                <FaChartLine style={{ color: "#38bdf8" }} /> Team Reports & Performance Stats
              </h3>
              <p>
                View reports and team stats based on hierarchy permissions.
                Team managers view their squad members; managers view all subordinates; CEO/Management can see all.
              </p>
            </div>

            {/* Team Filter */}
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ fontSize: "13px", color: "#cbd5e1" }}>Filter Team:</span>
              <select
                className="perm-select"
                style={{ background: "#334155", color: "#ffffff", borderColor: "#475569" }}
                value={statsTeamFilter}
                onChange={(e) => setStatsTeamFilter(e.target.value)}
              >
                <option value="">All Accessible Teams</option>
                {teams.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {statsLoading ? (
            <div style={{ padding: "60px", textAlign: "center" }}>
              <Spinner animation="border" variant="primary" />
              <p style={{ marginTop: "12px", color: "#64748b" }}>Calculating team stats and subordinate reports...</p>
            </div>
          ) : statsData ? (
            <>
              {/* Scope Explainer */}
              <div className="perm-controls-card" style={{ marginBottom: "20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div>
                  <span style={{ fontSize: "13px", color: "#64748b" }}>Viewer: </span>
                  <strong>{currentEmployee?.name}</strong>{" "}
                  <span className={`badge-hierarchy ${statsData.hierarchy_level}`} style={{ marginLeft: "8px" }}>
                    {statsData.hierarchy_level}
                  </span>
                  <span style={{ fontSize: "13px", color: "#475569", marginLeft: "14px" }}>
                    Scope: <strong>{statsData.can_view_all ? "Global Company Access" : statsData.team_name}</strong>
                  </span>
                </div>
                <div style={{ fontSize: "12.5px", color: "#64748b" }}>
                  Showing metrics for <strong>{statsData.total_members}</strong> active team members / subordinates
                </div>
              </div>

              {/* KPI Cards */}
              <div className="stats-kpi-grid">
                <div className="stats-kpi-card">
                  <div className="stats-kpi-icon" style={{ background: "#dcfce7", color: "#16a34a" }}>
                    <FaChartLine />
                  </div>
                  <div>
                    <div className="stats-kpi-val">${Number(statsData.total_sales).toLocaleString()}</div>
                    <div className="stats-kpi-label">Total Team Sales</div>
                  </div>
                </div>

                <div className="stats-kpi-card">
                  <div className="stats-kpi-icon" style={{ background: "#e0f2fe", color: "#0284c7" }}>
                    <FaUsers />
                  </div>
                  <div>
                    <div className="stats-kpi-val">{statsData.total_members}</div>
                    <div className="stats-kpi-label">Team Members</div>
                  </div>
                </div>

                <div className="stats-kpi-card">
                  <div className="stats-kpi-icon" style={{ background: "#fef3c7", color: "#d97706" }}>
                    <FaShieldAlt />
                  </div>
                  <div>
                    <div className="stats-kpi-val">{statsData.total_inquiries}</div>
                    <div className="stats-kpi-label">Inquiries Handled</div>
                  </div>
                </div>

                <div className="stats-kpi-card">
                  <div className="stats-kpi-icon" style={{ background: "#f3e8ff", color: "#9333ea" }}>
                    <FaCopy />
                  </div>
                  <div>
                    <div className="stats-kpi-val">{statsData.total_quotations}</div>
                    <div className="stats-kpi-label">Quotations Generated</div>
                  </div>
                </div>
              </div>

              {/* Leaderboard Table */}
              <div className="perm-table-card">
                <div style={{ padding: "16px 20px", borderBottom: "1px solid #e2e8f0", fontWeight: "700", fontSize: "15px" }}>
                  Team Members Performance Breakdown
                </div>
                <table className="perm-table">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Designation</th>
                      <th>Hierarchy Role</th>
                      <th>Total Sales</th>
                      <th>Inquiries</th>
                      <th>Quotations</th>
                      <th>Attendance Records</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statsData.members.length === 0 ? (
                      <tr>
                        <td colSpan={7} style={{ textAlign: "center", padding: "30px", color: "#94a3b8" }}>
                          No team member data available for current scope.
                        </td>
                      </tr>
                    ) : (
                      statsData.members.map((m) => (
                        <tr key={m.employee_id} style={{ background: m.is_self ? "#f0f9ff" : "inherit" }}>
                          <td>
                            <strong>{m.name}</strong>
                            {m.is_self && (
                              <span style={{ fontSize: "11px", background: "#bae6fd", color: "#0369a1", padding: "2px 6px", borderRadius: "8px", marginLeft: "6px" }}>
                                You
                              </span>
                            )}
                          </td>
                          <td style={{ color: "#475569" }}>{m.designation}</td>
                          <td>
                            <span className={`badge-hierarchy ${m.hierarchy_level}`}>
                              {m.hierarchy_level}
                            </span>
                          </td>
                          <td>
                            <strong style={{ color: "#16a34a" }}>${Number(m.sales_total).toLocaleString()}</strong>
                          </td>
                          <td>{m.inquiries_count}</td>
                          <td>{m.quotations_count}</td>
                          <td>
                            <span className="badge bg-light text-dark border">
                              {m.attendance_records} logs
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}
        </div>
      )}

      {/* ========================================================= */}
      {/* MODAL: ADD / EDIT DESIGNATION                             */}
      {/* ========================================================= */}
      <Modal show={showDesigModal} onHide={() => setShowDesigModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "18px", fontWeight: "700" }}>
            <FaIdBadge style={{ color: "#0284c7", marginRight: "8px" }} />
            {editingDesigId ? "Edit Designation" : "Create New Designation"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ marginBottom: "14px" }}>
            <label style={{ fontSize: "13px", fontWeight: "600", display: "block", marginBottom: "6px" }}>
              Designation Title:
            </label>
            <input
              type="text"
              className="form-control"
              placeholder="e.g., Regional Sales Director, Team Manager, Operations Specialist"
              value={desigForm.title}
              onChange={(e) => setDesigForm({ ...desigForm, title: e.target.value })}
            />
          </div>

          <div style={{ marginBottom: "14px" }}>
            <label style={{ fontSize: "13px", fontWeight: "600", display: "block", marginBottom: "6px" }}>
              Hierarchy Level:
            </label>
            <select
              className="form-select"
              value={desigForm.hierarchy_level}
              onChange={(e) => setDesigForm({ ...desigForm, hierarchy_level: e.target.value })}
            >
              <option value="management">Management / CEO (Global visibility across all company)</option>
              <option value="manager">Manager (Can see all subordinates and their teams)</option>
              <option value="team_manager">Team Manager (Can see team members and team stats)</option>
              <option value="subordinate">Executive / Subordinate (Can see own records only)</option>
            </select>
          </div>

          <div>
            <label style={{ fontSize: "13px", fontWeight: "600", display: "block", marginBottom: "6px" }}>
              Description:
            </label>
            <textarea
              className="form-control"
              rows={3}
              placeholder="Responsibilities, scope, and visibility privileges..."
              value={desigForm.description}
              onChange={(e) => setDesigForm({ ...desigForm, description: e.target.value })}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary btn-sm" onClick={() => setShowDesigModal(false)}>
            Cancel
          </button>
          <button className="btn btn-primary btn-sm" onClick={handleSaveDesignation} disabled={loading}>
            Save Designation
          </button>
        </Modal.Footer>
      </Modal>

      {/* ========================================================= */}
      {/* MODAL: ADD / EDIT TEAM & TAG MEMBERS                      */}
      {/* ========================================================= */}
      <Modal show={showTeamModal} onHide={() => setShowTeamModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "18px", fontWeight: "700" }}>
            <FaUsersCog style={{ color: "#0284c7", marginRight: "8px" }} />
            {editingTeamId ? "Edit Team & Tagged Members" : "Create Team & Tag Members"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row g-3">
            <div className="col-md-6">
              <label style={{ fontSize: "13px", fontWeight: "600", display: "block", marginBottom: "6px" }}>
                Team / Squad Name:
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g., Sales Alpha, Dubai Operations, Direct Billing Squad"
                value={teamForm.name}
                onChange={(e) => setTeamForm({ ...teamForm, name: e.target.value })}
              />
            </div>

            <div className="col-md-6">
              <label style={{ fontSize: "13px", fontWeight: "600", display: "block", marginBottom: "6px" }}>
                Department:
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g., Commercial, Finance, Operations"
                value={teamForm.department}
                onChange={(e) => setTeamForm({ ...teamForm, department: e.target.value })}
              />
            </div>

            <div className="col-md-12">
              <label style={{ fontSize: "13px", fontWeight: "600", display: "block", marginBottom: "6px" }}>
                Team Manager / Team Lead:
              </label>
              <select
                className="form-select"
                value={teamForm.team_manager_id}
                onChange={(e) => setTeamForm({ ...teamForm, team_manager_id: e.target.value })}
              >
                <option value="">-- Choose Team Manager --</option>
                {employees.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.name} ({emp.designation_title || "Staff"})
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-12">
              <label style={{ fontSize: "13px", fontWeight: "600", display: "block", marginBottom: "6px" }}>
                Tag Team Members (Select all who belong to this team):
              </label>
              <div
                style={{
                  maxHeight: "180px",
                  overflowY: "auto",
                  border: "1px solid #cbd5e1",
                  borderRadius: "8px",
                  padding: "10px",
                  background: "#f8fafc",
                }}
              >
                {employees.map((emp) => {
                  const isChecked = teamForm.member_ids.includes(emp.id);
                  return (
                    <div key={emp.id} className="form-check" style={{ marginBottom: "6px" }}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`team-member-${emp.id}`}
                        checked={isChecked}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setTeamForm({ ...teamForm, member_ids: [...teamForm.member_ids, emp.id] });
                          } else {
                            setTeamForm({
                              ...teamForm,
                              member_ids: teamForm.member_ids.filter((id) => id !== emp.id),
                            });
                          }
                        }}
                      />
                      <label className="form-check-label" htmlFor={`team-member-${emp.id}`} style={{ fontSize: "13px" }}>
                        <strong>{emp.name}</strong> — {emp.designation_title || "Staff"} ({emp.email || "No email"})
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="col-md-12">
              <label style={{ fontSize: "13px", fontWeight: "600", display: "block", marginBottom: "6px" }}>
                Description:
              </label>
              <textarea
                className="form-control"
                rows={2}
                placeholder="Team objectives, deliverables, and coverage..."
                value={teamForm.description}
                onChange={(e) => setTeamForm({ ...teamForm, description: e.target.value })}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary btn-sm" onClick={() => setShowTeamModal(false)}>
            Cancel
          </button>
          <button className="btn btn-primary btn-sm" onClick={handleSaveTeam} disabled={loading}>
            Save Team & Tagged Members
          </button>
        </Modal.Footer>
      </Modal>

      {/* ========================================================= */}
      {/* MODAL: CLONE PERMISSIONS                                  */}
      {/* ========================================================= */}
      <Modal show={showCloneModal} onHide={() => setShowCloneModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "18px", fontWeight: "700" }}>
            <FaCopy style={{ color: "#0284c7", marginRight: "8px" }} /> Duplicate Permissions
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ fontSize: "13px", color: "#475569" }}>
            Copy all module permissions from another employee and apply them to{" "}
            <strong>{currentEmployee?.name}</strong>.
          </p>
          <div style={{ marginTop: "16px" }}>
            <label style={{ fontSize: "13px", fontWeight: "600", marginBottom: "6px", display: "block" }}>
              Source Employee to copy from:
            </label>
            <select
              className="form-select"
              value={sourceEmpId}
              onChange={(e) => setSourceEmpId(e.target.value)}
            >
              <option value="">-- Choose Employee --</option>
              {employees
                .filter((e) => String(e.id) !== String(selectedEmployeeId))
                .map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.name} ({emp.designation_title || emp.role_name || "Custom"})
                  </option>
                ))}
            </select>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary btn-sm" onClick={() => setShowCloneModal(false)}>
            Cancel
          </button>
          <button className="btn btn-primary btn-sm" onClick={handleClone} disabled={!sourceEmpId || loading}>
            Apply & Duplicate
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PermissionsPage;
